const DEFAULT_OPTIONS = {
	columnStart: "A",
	columnEnd: "D",
	maxSeats: 200,
};

const RANGE_PATTERN = /^\s*(\d+)([A-Za-z])?\s*~\s*(\d+)([A-Za-z])?\s*$/;

export const parseSeatRange = (input, options = {}) => {
	const value = String(input ?? "").trim();
	if (!value.includes("~")) {
		return { isRange: false, seats: [], count: 0 };
	}

	const match = value.match(RANGE_PATTERN);
	if (!match) {
		return {
			isRange: true,
			seats: [],
			count: 0,
			error: "좌석 범위 형식이 올바르지 않습니다.",
		};
	}

	const [, startNumRaw, startCharRaw, endNumRaw, endCharRaw] = match;
	const startNum = parseInt(startNumRaw, 10);
	const endNum = parseInt(endNumRaw, 10);

	if (!Number.isFinite(startNum) || !Number.isFinite(endNum)) {
		return {
			isRange: true,
			seats: [],
			count: 0,
			error: "좌석 범위 숫자가 올바르지 않습니다.",
		};
	}

	if (startNum <= 0 || endNum <= 0) {
		return {
			isRange: true,
			seats: [],
			count: 0,
			error: "좌석 번호는 1 이상이어야 합니다.",
		};
	}

	if (startNum > endNum) {
		return {
			isRange: true,
			seats: [],
			count: 0,
			error: "시작 좌석이 끝 좌석보다 클 수 없습니다.",
		};
	}

	const startChar = startCharRaw ? startCharRaw.toUpperCase() : "";
	const endChar = endCharRaw ? endCharRaw.toUpperCase() : "";
	const hasAlpha = Boolean(startChar && endChar);
	const hasNumericOnly = !startChar && !endChar;

	if (!hasAlpha && !hasNumericOnly) {
		return {
			isRange: true,
			seats: [],
			count: 0,
			error: "시작/끝 좌석은 동일한 형식이어야 합니다.",
		};
	}

	const { columnStart, columnEnd, maxSeats } = {
		...DEFAULT_OPTIONS,
		...options,
	};

	const normalized = `${startNum}${startChar}~${endNum}${endChar}`;

	const seats = [];
	if (hasNumericOnly) {
		for (let num = startNum; num <= endNum; num += 1) {
			seats.push(String(num));
			if (seats.length > maxSeats) {
				return {
					isRange: true,
					seats: [],
					count: 0,
					normalized,
					error: `최대 ${maxSeats}석까지 생성할 수 있습니다.`,
				};
			}
		}

		return { isRange: true, seats, count: seats.length, normalized, mode: "numeric" };
	}

	const minCol = columnStart.toUpperCase();
	const maxCol = columnEnd.toUpperCase();
	const minColCode = minCol.charCodeAt(0);
	const maxColCode = maxCol.charCodeAt(0);
	const startCode = startChar.charCodeAt(0);
	const endCode = endChar.charCodeAt(0);

	if (
		startChar.length !== 1 ||
		endChar.length !== 1 ||
		startCode < minColCode ||
		startCode > maxColCode ||
		endCode < minColCode ||
		endCode > maxColCode
	) {
		return {
			isRange: true,
			seats: [],
			count: 0,
			normalized,
			error: `좌석 열은 ${minCol}~${maxCol}만 지원합니다.`,
		};
	}

	for (let row = startNum; row <= endNum; row += 1) {
		const rowStart = row === startNum ? startCode : minColCode;
		const rowEnd = row === endNum ? endCode : maxColCode;

		if (rowStart > rowEnd) {
			return {
				isRange: true,
				seats: [],
				count: 0,
				normalized,
				error: "좌석 열 범위가 올바르지 않습니다.",
			};
		}

		for (let code = rowStart; code <= rowEnd; code += 1) {
			seats.push(`${row}${String.fromCharCode(code)}`);
			if (seats.length > maxSeats) {
				return {
					isRange: true,
					seats: [],
					count: 0,
					normalized,
					error: `최대 ${maxSeats}석까지 생성할 수 있습니다.`,
				};
			}
		}
	}

	return { isRange: true, seats, count: seats.length, normalized, mode: "alpha" };
};

